/**
 * AtlasInstrumentTable — CoinMarketCap-style searchable/sortable instrument list.
 *
 * Columns: rank · ticker · name · price · 24h% · market cap
 * Rows are clickable (call onSelect with the instrument row).
 * Header toggles can sort asc/desc; free-text search filters by symbol or name.
 *
 * Kind filter pills: ALL / CRYPTO / COMMODITY / FX
 */
import React, { useMemo, useState } from 'react';

const KIND_COLOR = {
  crypto:    '#f7c94b',
  commodity: '#e8c878',
  fx:        '#7ecfc0',
};

function fmtPrice(p) {
  if (p == null) return '—';
  if (p < 1) return `$${p.toFixed(4)}`;
  if (p >= 1000) return `$${p.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  return `$${p.toFixed(2)}`;
}
function fmtMcap(m) {
  if (!m) return '—';
  if (m >= 1e12) return `$${(m / 1e12).toFixed(2)}T`;
  if (m >= 1e9)  return `$${(m / 1e9).toFixed(2)}B`;
  if (m >= 1e6)  return `$${(m / 1e6).toFixed(2)}M`;
  return `$${m.toFixed(0)}`;
}

function Pct({ v }) {
  if (v == null) return <span style={{ color: 'rgba(200,210,230,0.45)' }}>—</span>;
  const up = v >= 0;
  return (
    <span style={{
      color: up ? '#4ade80' : '#f87171',
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    }}>
      {up ? '▲ ' : '▼ '}{Math.abs(v).toFixed(2)}%
    </span>
  );
}

export default function AtlasInstrumentTable({
  items = [],
  selectedSymbol,
  onSelect,
  testId = 'atlas-instrument-table',
}) {
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [sortKey, setSortKey] = useState('rank');
  const [sortDir, setSortDir] = useState('asc');

  const rows = useMemo(() => {
    let out = items;
    if (kindFilter !== 'all') out = out.filter((i) => i.kind === kindFilter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter(
        (i) => i.symbol?.toLowerCase().includes(needle) || (i.name || '').toLowerCase().includes(needle)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const safe = (v) => (v == null ? Number.POSITIVE_INFINITY : v);
    return [...out].sort((a, b) => {
      if (sortKey === 'symbol') return dir * String(a.symbol).localeCompare(String(b.symbol));
      if (sortKey === 'name')   return dir * String(a.name || '').localeCompare(String(b.name || ''));
      return dir * (safe(a[sortKey]) - safe(b[sortKey]));
    });
  }, [items, q, kindFilter, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  };
  const arrow = (k) => (sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  return (
    <div
      data-testid={testId}
      style={{
        border: '1px solid rgba(120, 150, 200, 0.18)',
        background: 'rgba(5, 7, 11, 0.65)',
        borderRadius: 6,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Header controls */}
      <div style={{
        padding: '10px 12px 6px',
        borderBottom: '1px solid rgba(120, 150, 200, 0.12)',
        background: 'rgba(10, 14, 22, 0.5)',
      }}>
        <div style={{ fontSize: 10, letterSpacing: '0.28em', color: 'rgba(200,210,230,0.65)', marginBottom: 6 }}>
          INSTRUMENTS · {rows.length}/{items.length}
        </div>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search symbol or name…"
          data-testid="atlas-instrument-search"
          style={{
            width: '100%',
            padding: '6px 10px',
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(120, 150, 200, 0.2)',
            color: '#dbe3f2',
            fontSize: 12,
            borderRadius: 3,
            outline: 'none',
            marginBottom: 6,
          }}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          {(['all', 'crypto', 'commodity', 'fx']).map((k) => (
            <button
              key={k}
              onClick={() => setKindFilter(k)}
              data-testid={`atlas-filter-${k}`}
              style={{
                flex: 1,
                padding: '4px 0',
                fontSize: 9,
                letterSpacing: '0.2em',
                background: kindFilter === k ? `${KIND_COLOR[k] || '#4de2c0'}20` : 'transparent',
                color: kindFilter === k ? (KIND_COLOR[k] || '#4de2c0') : 'rgba(200,210,230,0.55)',
                border: `1px solid ${kindFilter === k ? KIND_COLOR[k] || '#4de2c0' : 'rgba(120,150,200,0.15)'}`,
                borderRadius: 3,
                cursor: 'pointer',
              }}
            >
              {k.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '30px 1fr 70px 70px 70px',
        padding: '6px 12px',
        fontSize: 9,
        letterSpacing: '0.2em',
        color: 'rgba(180,200,230,0.5)',
        borderBottom: '1px solid rgba(120, 150, 200, 0.12)',
      }}>
        <button onClick={() => toggleSort('rank')}   style={_headBtn}>#{arrow('rank')}</button>
        <button onClick={() => toggleSort('symbol')} style={_headBtn}>SYMBOL{arrow('symbol')}</button>
        <button onClick={() => toggleSort('price')}  style={{..._headBtn, textAlign: 'right'}}>PRICE{arrow('price')}</button>
        <button onClick={() => toggleSort('change_pct_24h')} style={{..._headBtn, textAlign: 'right'}}>24H{arrow('change_pct_24h')}</button>
        <button onClick={() => toggleSort('market_cap')}     style={{..._headBtn, textAlign: 'right'}}>MCAP{arrow('market_cap')}</button>
      </div>

      {/* Row list */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {rows.length === 0 && (
          <div style={{ padding: 14, fontSize: 11, color: 'rgba(200,210,230,0.4)' }}>No matches.</div>
        )}
        {rows.map((row) => {
          const sel = row.symbol === selectedSymbol;
          return (
            <button
              key={row.symbol}
              onClick={() => onSelect && onSelect(row)}
              data-testid={`atlas-instrument-row-${row.symbol}`}
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '30px 1fr 70px 70px 70px',
                padding: '7px 12px',
                background: sel ? 'rgba(77, 226, 192, 0.12)' : 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(90, 110, 140, 0.06)',
                borderLeft: sel ? '2px solid #4de2c0' : '2px solid transparent',
                color: '#dbe3f2',
                fontSize: 11,
                cursor: 'pointer',
                textAlign: 'left',
                alignItems: 'center',
                transition: 'background 80ms',
              }}
              onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = 'rgba(120,150,200,0.06)'; }}
              onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ color: 'rgba(180,200,230,0.55)', fontSize: 10 }}>{row.rank ?? '—'}</span>
              <span>
                <span style={{
                  display: 'inline-block',
                  width: 6, height: 6, borderRadius: 3, marginRight: 6,
                  background: KIND_COLOR[row.kind] || '#4de2c0',
                }} />
                <span style={{ color: '#f1f5f9', fontSize: 12 }}>{row.symbol}</span>
                <span style={{ color: 'rgba(180,200,230,0.5)', fontSize: 10, marginLeft: 6 }}>
                  {(row.name || '').slice(0, 14)}
                </span>
              </span>
              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#e8e8e8' }}>
                {fmtPrice(row.price)}
              </span>
              <span style={{ textAlign: 'right' }}>
                <Pct v={row.change_pct_24h} />
              </span>
              <span style={{ textAlign: 'right', color: 'rgba(200,210,230,0.65)', fontSize: 10 }}>
                {fmtMcap(row.market_cap)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const _headBtn = {
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  font: 'inherit',
  cursor: 'pointer',
  padding: 0,
  textAlign: 'left',
};
